import { FrameOption, ConfigState } from '@renderer/types/configState'

/**
 * Calculate the number of photos needed based on the selected frame
 * @param frameId - The ID of the selected frame
 * @param frames - Array of available frames
 * @returns Number of photos required for the frame
 */
export function calculateCaptureCount(
  frameId: string | null | undefined,
  frames: FrameOption[]
): number {
  if (!frameId) {
    console.warn('No frameId provided, defaulting to 4 photos')
    return 4
  }

  const selectedFrame = frames.find((frame) => frame._id === frameId)

  if (!selectedFrame) {
    console.warn('Frame not found, defaulting to 4 photos')
    return 4
  }

  // Count unique photo placeholders in frame
  const uniquePhotoTypes = new Set(
    selectedFrame.drawParams.images
      .map((image) => image.type)
      .filter((type) => type.startsWith('photo-'))
  )

  const count = uniquePhotoTypes.size

  if (count === 0) {
    console.warn('No photo placeholders found in frame, defaulting to 4 photos')
    return 4
  }

  return count
}

/**
 * Get the layout type for a frame (vertical or grid)
 * @param frameId - The ID of the selected frame
 * @param frames - Array of available frames
 * @returns Layout type: 'vertical' or 'grid'
 */
export function getFrameLayout(
  frameId: string | null | undefined,
  frames: FrameOption[]
): 'vertical' | 'grid' {
  if (!frameId) {
    return 'grid'
  }

  const selectedFrame = frames.find((frame) => frame._id === frameId)

  if (!selectedFrame) {
    return 'grid'
  }

  // Try to determine layout based on frame category
  // Note: This requires the configStore to be set via setConfigStore
  // For now, we'll check frame name as fallback
  let frameCategories: any[] = []
  try {
    // Try to access configStore if available
    if (typeof window !== 'undefined' && (window as any).__CONFIG_STORE__) {
      frameCategories = (window as any).__CONFIG_STORE__.getState()?.frameCategories || []
    }
  } catch (e) {
    // Ignore errors
  }
  const category = frameCategories.find((cat) =>
    cat.frames?.some((frame) => frame._id === selectedFrame._id)
  )

  if (category) {
    const categoryName = category.name.toLowerCase()
    if (categoryName.includes('3cut') || categoryName.includes('4cut2')) {
      return 'vertical'
    } else if (categoryName.includes('4cut1')) {
      return 'grid'
    }
  }

  // Fallback to frame name
  const frameName = selectedFrame.name.toLowerCase()
  if (frameName.includes('3cut') || frameName.includes('4cut2')) {
    return 'vertical'
  } else if (frameName.includes('4cut1')) {
    return 'grid'
  }

  return 'grid'
}

/**
 * Validate that a frame is selected
 * @param frameId - The ID of the selected frame
 * @returns True if frame is selected, false otherwise
 */
export function validateFrameSelection(frameId: string | null | undefined): boolean {
  return frameId !== null && frameId !== undefined && frameId !== ''
}

/**
 * Calculate the estimated session duration based on configuration
 * @param config - The project configuration
 * @param frameId - The ID of the selected frame
 * @param frames - Array of available frames
 * @returns Estimated duration in seconds
 */
export function calculateSessionDuration(
  config: ConfigState | null,
  frameId: string | null | undefined,
  frames: FrameOption[]
): number {
  if (!config) {
    return 300 // Default 5 minutes
  }

  const captureCount = calculateCaptureCount(frameId, frames)
  const captureSettings = config.pages.capture.settings
  const frameSettings = config.pages.frame.settings
  const retakeSettings = config.pages.retake.settings
  const filterSettings = config.pages.filter.settings
  const resultSettings = config.pages.result.settings

  // Base durations
  const welcomeTimeout = config.pages.welcome.settings.enabled ? 0 : 0 // No timeout for welcome
  const frameTimeout = frameSettings.enabled ? frameSettings.frameTimeout || 30 : 0
  const prepTimeout = captureSettings.prepTimeout || 5
  const captureCountdown = captureSettings.captureCountdown || 3
  const filterTimeout = filterSettings.enabled ? 30 : 0 // Estimate 30s for filter selection
  const resultTimeout = resultSettings.resultTimeout || 60

  // Calculate capture duration per photo
  const captureDurationPerPhoto = prepTimeout + captureCountdown

  // Account for retakes (assume 10% retake rate)
  const retakeRate = retakeSettings.enabled ? 0.1 : 0
  const totalCaptureDuration = captureDurationPerPhoto * captureCount * (1 + retakeRate)

  // Total duration
  const totalDuration =
    welcomeTimeout + frameTimeout + totalCaptureDuration + filterTimeout + resultTimeout

  return Math.ceil(totalDuration)
}

/**
 * Get photo layout information (count and layout type)
 * @param frameId - The ID of the selected frame
 * @param frames - Array of available frames
 * @returns Object with count and layout
 */
export function getPhotoLayoutInfo(
  frameId: string | null | undefined,
  frames: FrameOption[]
): { count: number; layout: 'vertical' | 'grid' } {
  const count = calculateCaptureCount(frameId, frames)
  const layout = getFrameLayout(frameId, frames)

  return { count, layout }
}

// Import useConfigStore for frameCategories access
// This is a workaround since we can't use hooks in utility functions
// let useConfigStore: any = null

export function setConfigStore(_store: any): void {
  // useConfigStore = store
}
